import { TeamsMeetingIdLocator, TeamsMeetingLinkLocator } from '@azure/communication-calling';
import { AzureCommunicationTokenCredential, CommunicationUserIdentifier } from '@azure/communication-common';
import {
  CallAndChatLocator,
  CallWithChatComposite,
  CallWithChatAdapter,
  createAzureCommunicationCallWithChatAdapter,
  CallWithChatCompositeOptions,
  fromFlatCommunicationIdentifier,
  COMPOSITE_LOCALE_PT_BR,
  DEFAULT_COMPOSITE_ICONS,
  CallCompositePage
} from '@azure/communication-react';
import { useMemo, useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import '../../pages/ComunicacaoPage/ComunicacaoPage.css';
import * as comunicacaoService from "../../services/ComunicacaoService";
import { telemetry } from '../../services/TelemetriaService';

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 300,
  bgcolor: 'background.paper',
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  display: 'flex',
  borderRadius: 4,
  flexDirection: 'column'
};
// initializeIcons();

export type ChamadaComChatProps = {
  userIdentifier: string;
  token: string;
  displayName: string;
  endpointUrl: string;
  locator: TeamsMeetingLinkLocator | CallAndChatLocator | TeamsMeetingIdLocator;
  options?: CallWithChatCompositeOptions;
  formFactor?: 'desktop' | 'mobile';
  lobby?: LobbyProps;
};

export type LobbyProps = {
  mensagemLobby?: string;
  imagemLobby?: string;
};

export const ChamadaComChatContainer = (props: ChamadaComChatProps): JSX.Element => {
  let locale = COMPOSITE_LOCALE_PT_BR;
  let icons = DEFAULT_COMPOSITE_ICONS;

  const [open, setOpen] = useState(false);
  //Personalização tela de entrada
  if (props.lobby?.mensagemLobby) {
    locale.strings.call.lobbyScreenWaitingToBeAdmittedTitle = props.lobby?.mensagemLobby;
    locale.strings.call.lobbyScreenConnectingToCallTitle = props.lobby?.mensagemLobby;
  }

  if (props.lobby?.imagemLobby) {
    icons.LobbyScreenConnectingToCall = <img alt="" src={`data:image/jpeg;base64,${props.lobby?.imagemLobby}`} />
    icons.LobbyScreenWaitingToBeAdmitted = <img alt="" src={`data:image/jpeg;base64,${props.lobby?.imagemLobby}`} />
  }

  const userId = useMemo(
    () => fromFlatCommunicationIdentifier(props.userIdentifier) as CommunicationUserIdentifier,
    [props.userIdentifier]
  );

  const tokenRefresher = async (): Promise<string> => {
    const tokenResponse = await comunicacaoService.GetUserAcs();
    return tokenResponse.resultado.token;
  };

  const credential = useMemo(() => new AzureCommunicationTokenCredential({
    token: props.token,
    tokenRefresher: tokenRefresher,
    refreshProactively: true
  }), [props.token]);

  const [adapter, setAdapter] = useState<CallWithChatAdapter | undefined>(undefined);

  useEffect(() => {
    let localAdapter: CallWithChatAdapter | undefined;
    let callStartTime: number = 0;
    let currentCallId: string | undefined;

    const handleCallEnded = (event: any) => {
      const duration = callStartTime ? (Date.now() - callStartTime) / 1000 : 0;
      const callId = currentCallId || 'unknown';

      if (event.callEndReason?.code && event.callEndReason.code !== 0) {
        telemetry.callEndedUnexpectedly(callId, event.callEndReason.code, event.callEndReason.subCode || 0);
      } else {
        telemetry.callEnded(callId, duration);
      }

      comunicacaoService.PostLogReuniao(localStorage.getItem('idReuniao') as string, localStorage.getItem('idAtor') as string, 'S');
      window.parent.postMessage("call:ended", "*");
      window.location.href = '?Instance=RmVlZGJhY2tQYWdl';
    };

    const handleParticipantsJoined = (event: any) => {
      setOpen(false);
      if (currentCallId) {
        telemetry.participantsJoined(currentCallId, event.joined?.length || 1);
      }
    };

    const handleStateChange = (state: any) => {
      if (state.page === "lobby" && (props.lobby?.mensagemLobby || props.lobby?.imagemLobby)) {
        setOpen(true);
      }
      if (state.call && Array.isArray(state.call?.remoteParticipants) && state.call.remoteParticipants.length > 1) {
        setOpen(false);
      }
      if (state.call?.state === 'Connected' && !callStartTime) {
        callStartTime = Date.now();
        if (currentCallId) {
          telemetry.callConnected(currentCallId);
        }
      }
    };

    const handleBeforeUnload = () => {
      if (currentCallId) {
        telemetry.pageUnload(currentCallId);
      }
      localAdapter?.leaveCall().catch(() => {});
    };

    const init = async () => {
      telemetry.adapterCreating();
      try {
        localAdapter = await createAzureCommunicationCallWithChatAdapter({
          userId: userId,
          displayName: props.displayName,
          credential,
          locator: props.locator,
          endpoint: props.endpointUrl,
        });

        telemetry.adapterCreated();

        localAdapter.on('callEnded', handleCallEnded);
        localAdapter.on('callParticipantsJoined', handleParticipantsJoined);
        localAdapter.onStateChange(handleStateChange);

        // Get callId when available
        const state = localAdapter.getState();
        if (state.call?.id) {
          currentCallId = state.call.id;
          telemetry.callConnecting(currentCallId);
        }

        setAdapter(localAdapter);
      } catch (error: any) {
        telemetry.adapterError(error.message || 'Unknown error', error.code);
        console.error('Failed to create adapter:', error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    init();

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (localAdapter) {
        localAdapter.off('callEnded', handleCallEnded);
        localAdapter.off('callParticipantsJoined', handleParticipantsJoined);
        localAdapter.dispose();
      }
    };
  }, [userId, props.displayName, credential, props.locator, props.endpointUrl]);

  const handleClose = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, reason: any) => {
    if (reason !== 'backdropClick') {
      setOpen(false);
    }
  };

  const handleSair = () => {
    setOpen(false);
    comunicacaoService.PostLogReuniao(localStorage.getItem('idReuniao') as string, localStorage.getItem('idAtor') as string, 'S');
    window.parent.postMessage("call:ended", "*");
    localStorage.setItem("exibePesquisaSatisfacao", "false");
    adapter?.leaveCall().catch((e) => {
      console.error(e);
    });
  };

  if (adapter !== undefined) {
    return (
      <div style={{ height: '100dvh', width: '100vw' }}>

        <CallWithChatComposite
          adapter={adapter}
          formFactor={props.formFactor}
          locale={locale}
          options={props.options}
        />
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="parent-modal-title"
          aria-describedby="parent-modal-description"
        >
          <Box sx={{ ...modalStyle, width: 300 }}>
            <img className="image-start" alt="" src={`data:image/jpeg;base64,${props.lobby?.imagemLobby}`} />
            <div className="modal-message">
              {props.lobby?.mensagemLobby}
            </div>
            <div className="modal-buttons" >
              <Button onClick={handleSair} style={{
                textTransform: 'capitalize', fontWeight: 'bold', minWidth: 130, backgroundColor: "#000080", color: 'white'
              }} variant="contained" disableRipple>Voltar</Button>
             
            </div>

          </Box>

        </Modal>
      </div>
    );
  }

  return <></>;

};